// Player Profile screen - View any player's profile, stats, and achievements
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { Card, Avatar } from '../components';
import HamburgerMenu from '../components/HamburgerMenu';
import { getPlayerAchievements } from '../services';
import { getOverallLeaderboard, getCategoryLeaderboard } from '../utils/leaderboard';

const PlayerProfile = () => {
  const { competitionId, playerId } = useParams();
  const navigate = useNavigate();
  const { competition, players, categories, games, results } = useCompetition();

  const [achievements, setAchievements] = useState([]);
  const [loadingAchievements, setLoadingAchievements] = useState(true);

  const player = useMemo(() => {
    return players.find(p => p.id === playerId);
  }, [players, playerId]);

  // Load player achievements
  useEffect(() => {
    const loadAchievements = async () => {
      if (!playerId) return;

      try {
        setLoadingAchievements(true);
        const playerAchievements = await getPlayerAchievements(playerId);
        setAchievements(playerAchievements);
      } catch (error) {
        console.error('Error loading achievements:', error);
      } finally {
        setLoadingAchievements(false);
      }
    };

    loadAchievements();
  }, [playerId]);

  // Calculate player stats
  const playerStats = useMemo(() => {
    if (!player || !players.length) return null;

    const overallLeaderboard = getOverallLeaderboard(players, games, results, categories, competition?.rankingMethod || 'byCategory');
    const playerData = overallLeaderboard.find(p => p.playerId === player.id);
    const playerRank = overallLeaderboard.findIndex(p => p.playerId === player.id) + 1;

    // Calculate total actual points and games played
    const playerResults = results.filter(r => r.playerId === player.id && r.participated);
    const totalActualPoints = playerResults.reduce((sum, r) => sum + (r.pointsEntered || 0), 0);
    const gamesPlayed = playerResults.length;
    const averagePoints = gamesPlayed > 0 ? totalActualPoints / gamesPlayed : 0;

    // Category rankings
    const categoryRankings = categories
      .filter(cat => cat.status === 'active')
      .map(category => {
        const leaderboard = getCategoryLeaderboard(players, games, results, category.id);
        const playerCategoryData = leaderboard.find(p => p.playerId === player.id);
        const rank = leaderboard.findIndex(p => p.playerId === player.id) + 1;

        return {
          category,
          rank: rank > 0 ? rank : null,
          rankingPoints: playerCategoryData?.score || 0,
          actualScore: playerCategoryData?.actualScore || 0,
          gamesPlayed: playerCategoryData?.gamesPlayed || 0
        };
      });

    // Get all games this player participated in
    const playerGames = games
      .filter(g => g.status === 'played')
      .map(game => {
        const gameResults = results.filter(r => r.gameId === game.id && r.participated);
        const playerResult = gameResults.find(r => r.playerId === player.id);

        if (!playerResult || !playerResult.participated) return null;

        // Calculate position in this game
        const sortedResults = gameResults
          .map(r => ({ ...r, points: r.pointsEntered || 0 }))
          .sort((a, b) => b.points - a.points);

        const position = sortedResults.findIndex(r => r.playerId === player.id) + 1;
        const category = categories.find(c => c.id === game.categoryId);

        return {
          game,
          category,
          points: playerResult.pointsEntered || 0,
          position,
          totalPlayers: gameResults.length,
          eliminationPosition: playerResult.eliminationPosition
        };
      })
      .filter(g => g !== null)
      .sort((a, b) => new Date(b.game.date) - new Date(a.game.date));

    return {
      overallRank: playerRank > 0 ? playerRank : null,
      overallRankingPoints: playerData?.score || 0,
      totalActualPoints,
      gamesPlayed,
      averagePoints,
      categoryRankings,
      playerGames
    };
  }, [player, players, games, results, categories, competition]);

  if (!player) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Player not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <HamburgerMenu />
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Player Profile</p>
          <h1 className="text-3xl font-bold text-gray-900">{player.displayName}</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Profile Photo & Basic Info */}
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Photo */}
            <Avatar player={player} size="2xl" />

            {/* Basic Info */}
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl font-bold mb-2">{player.displayName}</h2>
              <p className="text-gray-600 mb-4">Competing in {competition.name}</p>
              {player.isOrganizer && (
                <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  Organizer
                </span>
              )}
            </div>
          </div>
        </Card>

        {/* Current Competition Stats */}
        {playerStats && (
          <>
            <Card title="Overall Standing" className="mb-6">
              <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-300">
                <div className="text-6xl font-bold text-blue-600 mb-2">
                  {playerStats.overallRank ? `#${playerStats.overallRank}` : '-'}
                </div>
                <div className="text-lg text-gray-700 font-medium mb-1">Overall Rank</div>
                <div className="text-sm text-gray-600">
                  {playerStats.overallRankingPoints} ranking points
                </div>
              </div>
            </Card>

            <Card title="Competition Stats" className="mb-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-gray-900">{playerStats.totalActualPoints.toFixed(1)}</div>
                  <div className="text-sm text-gray-600 mt-1">Total Points</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-gray-900">{playerStats.gamesPlayed}</div>
                  <div className="text-sm text-gray-600 mt-1">Games Played</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-gray-900">
                    {playerStats.averagePoints.toFixed(1)}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Avg Points/Game</div>
                </div>
              </div>

              {/* Category Rankings */}
              {playerStats.categoryRankings.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Category Rankings</h3>
                  <div className="space-y-2">
                    {playerStats.categoryRankings.map(cat => (
                      <div
                        key={cat.category.id}
                        className="flex items-center justify-between p-3 rounded-lg"
                        style={{ backgroundColor: `${cat.category.color}15` }}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{cat.category.icon}</span>
                          <span className="font-medium">{cat.category.name}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-lg">
                            {cat.rank ? `#${cat.rank}` : '-'}
                          </div>
                          <div className="text-sm text-gray-600">
                            {cat.rankingPoints} ranking pts • {cat.actualScore.toFixed(1)} total pts
                          </div>
                          <div className="text-xs text-gray-500">
                            {cat.gamesPlayed} games
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* All Games */}
            {playerStats.playerGames.length > 0 && (
              <Card title="All Games" className="mb-6">
                <div className="space-y-2">
                  {playerStats.playerGames.map((gameData, index) => {
                    const positionColor =
                      gameData.position === 1 ? 'bg-yellow-100 border-yellow-400 text-yellow-800' :
                      gameData.position === 2 ? 'bg-gray-200 border-gray-400 text-gray-800' :
                      gameData.position === 3 ? 'bg-orange-100 border-orange-400 text-orange-800' :
                      'bg-gray-50 border-gray-300 text-gray-700';

                    const positionEmoji =
                      gameData.position === 1 ? '🥇' :
                      gameData.position === 2 ? '🥈' :
                      gameData.position === 3 ? '🥉' :
                      `#${gameData.position}`;

                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg border-2 hover:shadow-sm transition-shadow"
                        style={{
                          backgroundColor: `${gameData.category?.color || '#3B82F6'}08`,
                          borderColor: `${gameData.category?.color || '#3B82F6'}30`
                        }}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold border-2 ${positionColor}`}>
                            {positionEmoji}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{gameData.game.name}</div>
                            <div className="text-xs text-gray-500">
                              {gameData.category?.icon} {gameData.category?.name} • {new Date(gameData.game.date).toLocaleDateString('en-GB')}
                            </div>
                            {gameData.eliminationPosition && (
                              <div className="text-xs text-blue-600 mt-1">
                                🎯 Knockout: {gameData.eliminationPosition === 1 ? '1st' : gameData.eliminationPosition === 2 ? '2nd' : gameData.eliminationPosition === 3 ? '3rd' : `${gameData.eliminationPosition}th`} eliminated
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-gray-900">{gameData.points.toFixed(1)}</div>
                          <div className="text-xs text-gray-500">
                            {gameData.position}/{gameData.totalPlayers} players
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </>
        )}

        {/* Previous Records / Achievements */}
        <Card title="Previous Records & Achievements">
          {loadingAchievements ? (
            <div className="text-center py-8 text-gray-500">Loading achievements...</div>
          ) : achievements.length > 0 ? (
            <div className="space-y-3">
              {achievements.map(achievement => (
                <div
                  key={achievement.id}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg border-2 border-yellow-300"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">
                      {achievement.type === 'overall_winner' ? '🏆' : '🥇'}
                    </div>
                    <div>
                      <div className="font-semibold text-lg">
                        {achievement.type === 'overall_winner'
                          ? 'Overall Winner'
                          : `${achievement.categoryName} Winner`}
                      </div>
                      <div className="text-sm text-gray-600">
                        {achievement.competitionName} • {achievement.year}
                      </div>
                      {achievement.notes && (
                        <div className="text-xs text-gray-500 mt-1">{achievement.notes}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-yellow-600">{achievement.year}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">🏅</div>
              <p className="text-gray-500">No achievements yet</p>
            </div>
          )}
        </Card>

        {/* Version Info */}
        <div className="text-center py-4 text-xs text-gray-400">
          v{require('../../package.json').version}
        </div>
      </div>
    </div>
  );
};

export default PlayerProfile;
